import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { 
  Brain, Upload, FileText, CheckCircle, AlertCircle, Clock,
  Trash2, RefreshCw, Loader2, Database, Zap, Settings, Plus, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function AITraining() {
  const { token } = useAuth();
  const [trainingData, setTrainingData] = useState([]);
  const [stats, setStats] = useState({
    totalDocuments: 0,
    lastTraining: '2025-05-20T14:00:00Z',
    accuracy: 94.5,
    pendingUpdates: 0
  });
  const [loading, setLoading] = useState(false);
  const [training, setTraining] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Sample training data for visual fallback
  const sampleTrainingData = [
    {
      id: 1,
      name: 'Product Documentation v2.5',
      type: 'PDF',
      size: '4.2 MB',
      status: 'trained',
      uploaded_at: '2025-05-15T10:00:00Z',
      trained_at: '2025-05-15T10:30:00Z'
    },
    {
      id: 2,
      name: 'FAQ Database Export',
      type: 'CSV',
      size: '1.8 MB',
      status: 'trained',
      uploaded_at: '2025-05-12T09:00:00Z',
      trained_at: '2025-05-12T10:00:00Z'
    },
    {
      id: 3,
      name: 'Troubleshooting Guide 2025',
      type: 'DOCX',
      size: '2.1 MB',
      status: 'pending',
      uploaded_at: '2025-05-20T14:00:00Z',
      trained_at: null
    }
  ];

  const fetchTrainingData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/support/training-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrainingData(data.documents || []);
        if (data.stats) {
          setStats(data.stats);
        }
      } else {
        toast.error('Failed to load training documents');
      }
    } catch (error) {
      console.error('Error fetching training data:', error);
      toast.error('Failed to load training documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainingData();
  }, [token]);

  const displayData = trainingData.length > 0 ? trainingData : sampleTrainingData;

  const getStatusBadge = (status) => {
    const badges = {
      'trained': { class: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle, text: 'Trained' },
      'pending': { class: 'bg-amber-500/20 text-amber-400', icon: Clock, text: 'Pending' },
      'training': { class: 'bg-blue-500/20 text-blue-400', icon: RefreshCw, text: 'Training' },
      'failed': { class: 'bg-red-500/20 text-red-400', icon: AlertCircle, text: 'Failed' },
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.class}`}>
        <badge.icon className={`w-3 h-3 ${status === 'training' ? 'animate-spin' : ''}`} />
        {badge.text}
      </span>
    );
  };

  const handleStartTraining = async () => {
    setTraining(true);
    try {
      const response = await fetch(`${API}/api/support/train`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.ok) {
        toast.success('Training completed successfully');
        await fetchTrainingData();
      } else {
        toast.error('Training failed');
      }
    } catch (error) {
      console.error('Training error:', error);
      toast.error('Training failed');
    } finally {
      setTraining(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setLoading(true);
    try {
      let successCount = 0;
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API}/api/support/training-data/upload`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: formData
        });
        
        if (response.ok) {
          successCount++;
        } else {
          const err = await response.json();
          toast.error(`Failed to upload ${file.name}: ${err.detail || 'Error'}`);
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} file(s) uploaded successfully`);
        await fetchTrainingData();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload files');
    } finally {
      setLoading(false);
      setShowUploadModal(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    // If it's a mock document ID (numeric), just filter it out locally as fallback
    if (typeof docId === 'number' || docId.length < 5) {
      setTrainingData(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted successfully');
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/support/training-data/${docId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        toast.success('Document deleted successfully');
        await fetchTrainingData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete document');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="w-6 h-6 text-primary" />
            AI Training
          </h1>
          <p className="text-muted-foreground">Train and manage AI knowledge base</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowUploadModal(true)}
            className="support-btn-secondary flex items-center gap-2"
            data-testid="upload-data-btn"
          >
            <Upload className="w-4 h-4" />
            Upload Data
          </button>
          <button
            onClick={handleStartTraining}
            disabled={training || stats.pendingUpdates === 0}
            className="support-btn-primary flex items-center gap-2"
            data-testid="start-training-btn"
          >
            {training ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Training...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Start Training
              </>
            )}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="metric-card">
          <div className="flex items-center gap-2 mb-2">
            <Database className="w-5 h-5 text-primary" />
          </div>
          <p className="text-2xl font-bold">{stats.totalDocuments}</p>
          <p className="text-xs text-muted-foreground">Total Documents</p>
        </div>

        <div className="metric-card">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-5 h-5 text-emerald-400" />
          </div>
          <p className="text-2xl font-bold text-emerald-400">{stats.accuracy}%</p>
          <p className="text-xs text-muted-foreground">AI Accuracy</p>
        </div>

        <div className="metric-card">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-amber-400" />
          </div>
          <p className="text-2xl font-bold text-amber-400">{stats.pendingUpdates}</p>
          <p className="text-xs text-muted-foreground">Pending Updates</p>
        </div>

        <div className="metric-card">
          <div className="flex items-center gap-2 mb-2">
            <RefreshCw className="w-5 h-5 text-blue-400" />
          </div>
          <p className="text-sm font-medium text-blue-400">
            {new Date(stats.lastTraining).toLocaleDateString()}
          </p>
          <p className="text-xs text-muted-foreground">Last Training</p>
        </div>
      </div>

      {/* Training Progress */}
      {training && (
        <div className="support-card border-primary/30">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Brain className="w-5 h-5 text-primary animate-pulse" />
            </div>
            <div>
              <h3 className="font-semibold">Training in Progress</h3>
              <p className="text-sm text-muted-foreground">Processing documents and updating AI model...</p>
            </div>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full animate-pulse w-2/3"></div>
          </div>
        </div>
      )}

      {/* Training Data Table */}
      <div className="support-card overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-semibold">Training Documents</h3>
          <span className="text-sm text-muted-foreground">{displayData.length} documents</span>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Document</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Size</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Uploaded</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Trained</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayData.map((item) => (
                <tr key={item.id} className="border-b border-border/50 hover:bg-secondary/30 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="font-medium text-sm">{item.name}</span>
                    </div>
                    {item.error && (
                      <p className="text-xs text-red-400 mt-1">{item.error}</p>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-xs bg-secondary px-2 py-1 rounded">{item.type}</span>
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">{item.size}</td>
                  <td className="py-3 px-4">{getStatusBadge(item.status)}</td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">{formatDate(item.uploaded_at)}</td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">{formatDate(item.trained_at)}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      {item.status === 'failed' && (
                        <button className="p-1.5 hover:bg-secondary rounded transition-colors">
                          <RefreshCw className="w-4 h-4 text-muted-foreground" />
                        </button>
                      )}
                      <button 
                        onClick={() => handleDeleteDocument(item.id)}
                        className="p-1.5 hover:bg-red-500/20 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Configuration */}
      <div className="support-card">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">AI Configuration</h3>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">Response Temperature</label>
            <input
              type="range"
              min="0"
              max="100"
              defaultValue="70"
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Conservative</span>
              <span>Creative</span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Maximum Response Length</label>
            <select className="support-input">
              <option value="short">Short (100 words)</option>
              <option value="medium" selected>Medium (250 words)</option>
              <option value="long">Long (500 words)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Confidence Threshold</label>
            <input
              type="range"
              min="0"
              max="100"
              defaultValue="85"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Escalate to human if AI confidence is below this threshold
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Auto-Training</label>
            <div className="flex items-center gap-3">
              <button className="relative w-12 h-6 bg-primary rounded-full transition-colors">
                <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></span>
              </button>
              <span className="text-sm text-muted-foreground">Train automatically when new data is uploaded</span>
            </div>
          </div>
        </div>

        <button className="support-btn-primary mt-6">
          Save Configuration
        </button>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-lg p-6 animate-fade-in" data-testid="upload-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Upload Training Data</h2>
              <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-secondary rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary/50 transition-colors">
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.txt,.csv,.xlsx"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="font-medium mb-2">Drag and drop files here</p>
                <p className="text-sm text-muted-foreground mb-4">or click to browse</p>
                <p className="text-xs text-muted-foreground">
                  Supported formats: PDF, DOC, DOCX, TXT, CSV, XLSX
                </p>
              </label>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowUploadModal(false)}
                className="flex-1 support-btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
